import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../api/client";
import { formatDate, formatRelative } from "../../lib/admin/adminFormat";

/**
 * Общий список людей обоих контуров, по алфавиту.
 *
 * Школьные и внешние остаются каждый в своей админке — там свои сводки и
 * свои настройки. Этот список отвечает на другой вопрос: «есть ли у нас
 * вообще такой человек», когда заранее не знаешь, в какой он половине.
 */

type AdminUserRow = {
  id: string;
  email: string;
  account_kind: "school" | "external";
  created_at: string;
  plan: { code: string } | null;
};

const PAGE_SIZE = 100;

export function AdminAllUsersTab({ onPick }: { onPick?: (userId: string) => void }) {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState("");
  const [applied, setApplied] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(
    async (opts: { offset: number; q: string; append: boolean }) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          kind: "all",
          limit: String(PAGE_SIZE),
          offset: String(opts.offset),
        });
        if (opts.q) params.set("q", opts.q);
        const data = await apiFetch<{ users: AdminUserRow[]; total: number }>(
          `/api/v1/admin/users?${params.toString()}`
        );
        setUsers((prev) => (opts.append ? [...prev, ...(data.users ?? [])] : data.users ?? []));
        setTotal(data.total ?? 0);
        setOffset(opts.offset);
        setErr("");
      } catch (e) {
        setErr(
          e instanceof Error
            ? e.message
            : "Не удалось загрузить общий список"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    void load({ offset: 0, q: "", append: false });
  }, [load]);

  const canLoadMore = users.length < total;

  return (
    <div className="wb-admin-users">
      <div className="wb-admin-users__head">
        <h2 className="wb-admin-users__title">
          Все пользователи{" "}
          <span className="wb-admin-users__count">
            {users.length} из {total}
          </span>
        </h2>
        <form
          className="wb-cabinet-panel-create__form"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(filter.trim());
            void load({ offset: 0, q: filter.trim(), append: false });
          }}
        >
          <input
            className="wb-cabinet-panel-create__input"
            type="search"
            placeholder="Фильтр по email…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button
            className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
            type="submit"
            disabled={loading}
          >
            Фильтр
          </button>
        </form>
      </div>

      {err ? <p className="wb-cabinet-plan-hint__text">{err}</p> : null}

      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Где</th>
              <th>Тариф</th>
              <th>Создан</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                className="wb-admin-table__row"
                onClick={() => onPick?.(u.id)}
              >
                <td>{u.email}</td>
                <td>{u.account_kind === "school" ? "Школа" : "Внешний"}</td>
                <td>{(u.plan?.code ?? (u.account_kind === "school" ? "school" : "free")).toUpperCase()}</td>
                <td title={formatDate(u.created_at)}>{formatRelative(u.created_at)}</td>
              </tr>
            ))}
            {!users.length && !loading ? (
              <tr>
                <td colSpan={4}>
                  {applied ? "Никого не нашлось" : "Пользователей пока нет"}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {canLoadMore ? (
        <button
          type="button"
          className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
          disabled={loading}
          onClick={() =>
            void load({ offset: offset + PAGE_SIZE, q: applied, append: true })
          }
        >
          {loading ? "Загрузка…" : "Показать ещё"}
        </button>
      ) : null}
    </div>
  );
}
